import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TrendChart = ({ sensorData, type = 'npk' }) => {
  // Utility function to format values with appropriate units
  const formatValueWithUnit = (label, value) => {
    // NPK units
    if (label.includes('Nitrogen') || label.includes('Phosphorous') || label.includes('Potassium')) {
      return `${label}: ${value} mg/kg`;
    }
    // Soil metrics units
    else if (label.includes('Temperature')) {
      return `${label}: ${value} °C`;
    }
    else if (label.includes('Moisture')) {
      return `${label}: ${value} %`;
    }
    else if (label.includes('EC')) {
      return `${label}: ${value} μS/cm`;
    }
    else if (label.includes('pH')) {
      return `${label}: ${value} pH`;
    }
    // Air metrics units
    else if (label.includes('Humidity')) {
      return `${label}: ${value} %`;
    }
    else if (label.includes('VPD')) {
      return `${label}: ${value} kPa`;
    }
    else if (label.includes('Solar Rad')) {
      return `${label}: ${value} W/m²`;
    }
    // Weather units are already included in the labels
    // Default case
    return `${label}: ${value}`;
  };
  
  // Generate mock historical data for demonstration
  let chartData, chartOptions;

  if (type === 'npk' && sensorData && sensorData.length > 0) {
    const labels = sensorData.map(d => new Date(d.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
    const nitrogenData = sensorData.map(d => parseInt(d.nitrogen));
    const phosphorousData = sensorData.map(d => parseInt(d.phosphorous));
    const potassiumData = sensorData.map(d => parseInt(d.potassium));

    chartData = {
      labels,
      datasets: [
        {
          label: 'Nitrogen (N)',
          data: nitrogenData,
          borderColor: '#38a169',
          backgroundColor: 'rgba(56, 161, 105, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Phosphorous (P)',
          data: phosphorousData,
          borderColor: '#3182ce',
          backgroundColor: 'rgba(49, 130, 206, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Potassium (K)',
          data: potassiumData,
          borderColor: '#d69e2e',
          backgroundColor: 'rgba(214, 158, 46, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
      ],
    };

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
          },
        },
        title: {
          display: true,
          text: 'NPK Levels - 24 Hour Trend',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              return formatValueWithUnit(context.dataset.label, context.parsed.y);
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Time',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Level',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
    };
  } else if (type === 'weather' && sensorData && sensorData.length > 0) {
    const labels = sensorData.map(d => new Date(d.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
    const windData = sensorData.map(d => Number(d.windSpeed));
    const rainfallData = sensorData.map(d => Number(d.totalRainfall));

    chartData = {
      labels,
      datasets: [
        {
          label: 'Wind Speed (km/h)',
          data: windData,
          borderColor: '#2196F3',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
          yAxisID: 'y',
        },
        {
          label: 'Rainfall (mm)',
          data: rainfallData,
          borderColor: '#4CAF50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
          yAxisID: 'y1',
        },
      ],
    };

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
          },
        },
        title: {
          display: true,
          text: 'Weather Conditions - 24 Hour Trend',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              return formatValueWithUnit(context.dataset.label, context.parsed.y);
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Time',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Wind Speed (km/h)',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Rainfall (mm)',
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    };
  } else if (type === 'soil' && sensorData && sensorData.length > 0) {
    const labels = sensorData.map(d => new Date(d.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
    const tempData = sensorData.map(d => Number(d.soiltemp));
    const moistureData = sensorData.map(d => Number(d.soilmoisture));
    const ecData = sensorData.map(d => Number(d.soilEC));
    const phData = sensorData.map(d => Number(d.soilPH));

    chartData = {
      labels,
      datasets: [
        {
          label: 'Soil Temperature (°C)',
          data: tempData,
          borderColor: '#FF9800',
          backgroundColor: 'rgba(255, 152, 0, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Soil Moisture (%)',
          data: moistureData,
          borderColor: '#4CAF50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Soil EC (uS/cm)',
          data: ecData,
          borderColor: '#9C27B0',
          backgroundColor: 'rgba(156, 39, 176, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Soil pH',
          data: phData,
          borderColor: '#03A9F4',
          backgroundColor: 'rgba(3, 169, 244, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
      ],
    };

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
          },
        },
        title: {
          display: true,
          text: 'Soil Metrics - 24 Hour Trend',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              return formatValueWithUnit(context.dataset.label, context.parsed.y);
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Time',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Value',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
    };
  } else if (type === 'air' && sensorData && sensorData.length > 0) {
    const labels = sensorData.map(d => new Date(d.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
    const airTempData = sensorData.map(d => Number(d.airTemp));
    const airHumidityData = sensorData.map(d => Number(d.airHumidity));
    const vpdData = sensorData.map(d => Number(d.vpd));
    const solarRadData = sensorData.map(d => Number(d.solarRad));

    chartData = {
      labels,
      datasets: [
        {
          label: 'Air Temp (°C)',
          data: airTempData,
          borderColor: '#FF5722',
          backgroundColor: 'rgba(255, 87, 34, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Air Humidity (%)',
          data: airHumidityData,
          borderColor: '#2196F3',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'VPD (kPa)',
          data: vpdData,
          borderColor: '#9C27B0',
          backgroundColor: 'rgba(156, 39, 176, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
        {
          label: 'Solar Rad (W/m²)',
          data: solarRadData,
          borderColor: '#FFC107',
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          fill: false,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 6,
        },
      ],
    };

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
          },
        },
        title: {
          display: true,
          text: 'Air Metrics - 24 Hour Trend',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              return formatValueWithUnit(context.dataset.label, context.parsed.y);
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Time',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Value',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
    };
  }

  if (!chartData) {
    return (
      <div style={{ 
        height: '300px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#666'
      }}>
        No data available for trend analysis
      </div>
    );
  }

  return <Line data={chartData} options={chartOptions} />;
};

export default TrendChart;
